import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { 
    MenuOutlined, 
    ChevronLeft, 
    Close, 
    LogoutOutlined 
} from "@mui/icons-material";
import { setLogout } from "state";
import { NAV_HEIGHT } from "state/constants";
import NavItems from "components/NavItems";
import FlexColumn from "components/FlexColumn";


const OPENED_WIDTH = 240;
const COLLAPSED_WIDTH = 68;

const Sidebar = ({ 
    isMobileOpen, 
    onClose, 
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const user = useSelector((state) => state.auth.user);
    const fullName = user?.crewName || 'Unknown';

    const [isCollapsed, setIsCollapsed] = useState(true);
    const effectiveCollapsed = isMobile ? false : isCollapsed;

    const handleToggle = () => setIsCollapsed((prev) => !prev);

    const handleLogOut = () => {
        localStorage.removeItem("rememberedEmail");
        localStorage.removeItem("rememberedPassword");
        dispatch(setLogout());
    };

    const navItems = NavItems();

    const logoutItem = {
        text: "Log Out",
        icon: <LogoutOutlined />,
        path: "login",
        action: handleLogOut,
    };

    const renderItem = (item) => {
        // Section header
        if (!item.path) {
            return (
                <Typography
                    key={item.text}
                    variant="h6"
                    color={theme.palette.primary.dark}
                    sx={{
                        px: effectiveCollapsed ? 0 : 2,
                        py: 1.5,
                        textAlign: effectiveCollapsed ? "center" : "left",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                    }}
                >
                    {/* {effectiveCollapsed ? item.text[0] : item.text} */}
                    {item.text}
                </Typography>
            );
        }

        const isActive = location.pathname === `/${item.path}`;

        return (
            <ListItem key={item.text} disablePadding>
                <Tooltip title={effectiveCollapsed ? item.text : ""} placement="right" arrow>
                    <ListItemButton
                        selected={isActive}
                        onClick={() => {
                            if (item.action) {
                                item.action();
                            } else {
                                navigate(`/${item.path}`);
                                if (isMobile && onClose) onClose();
                            }
                        }}
                        sx={{
                            justifyContent: effectiveCollapsed ? "center" : "flex-start",
                            py: 1.5,
                            borderLeft: "3px solid",
                            borderColor: isActive ? theme.palette.primary.main : "transparent",
                            color: isActive ? theme.palette.primary.main : "inherit",
                            fontWeight: isActive ? 700 : 400,
                            "&.Mui-selected": {
                                color: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                            },
                            "&.Mui-selected:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.18),
                            },
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: effectiveCollapsed ? 0 : 2,
                                justifyContent: "center",
                                color: isActive ? theme.palette.primary.main : "inherit",
                            }}
                        >
                            {item.icon}
                        </ListItemIcon>
                        {!effectiveCollapsed && (
                            <ListItemText primary={item.text} />
                        )}
                    </ListItemButton>
                </Tooltip>
            </ListItem>
        );
    };

    return (
        <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            open={isMobile ? isMobileOpen : true}
            onClose={onClose}
            ModalProps={{ keepMounted: true }}
            sx={{
                width: effectiveCollapsed ? COLLAPSED_WIDTH : OPENED_WIDTH,
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                    width: effectiveCollapsed ? COLLAPSED_WIDTH : OPENED_WIDTH,
                    transition: "width 0.3s ease",
                    overflowX: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "background.default",
                },
            }}
        >
            {/* STICKY HEADER */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 99,
                    height: NAV_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: effectiveCollapsed ? "center" : "space-between",
                    px: 2,
                    bgcolor: "background.alt",
                    flexShrink: 0,
                }}
            >
                {!effectiveCollapsed && (
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        color={theme.palette.primary.dark}
                        onClick={() => navigate("/")}
                        sx={{
                            cursor: "pointer",
                            "&:hover": { color: theme.palette.primary.main },
                        }}
                    >
                        DIGITAIR
                    </Typography>
                )}
                <Box display="flex" gap={1}>
                    {isMobile && (
                        <IconButton onClick={handleLogOut}>
                            <LogoutOutlined />
                        </IconButton>
                    )}
                    <IconButton onClick={isMobile ? onClose : handleToggle} size="small">
                        {isMobile ? <Close /> : effectiveCollapsed ? <MenuOutlined /> : <ChevronLeft />}
                    </IconButton>
                </Box>
            </Box>

            {/* SCROLLABLE CONTENT */}
            <Box sx={{ overflowY: "auto", flex: 1 }}>
                {!effectiveCollapsed && (
                    <FlexColumn mt={2} mb={1}>
                        <Typography variant="h5" color={theme.palette.primary.dark}>
                            {fullName}
                        </Typography>
                    </FlexColumn>
                )}

                <List dense>{navItems.map((item) => renderItem(item))}</List>
                <List dense>{renderItem(logoutItem)}</List>

                {!effectiveCollapsed && (
                    <FlexColumn gap={1} my={2} px={2}>
                        <Typography variant="subtitle2">
                            {`Copyright ©${new Date().getFullYear()} by`}
                        </Typography>
                        <Typography variant="h6" color={theme.palette.primary.dark}>
                            RAD Manager
                        </Typography>
                    </FlexColumn>
                )}
            </Box>
        </Drawer>
    );
};

export default Sidebar;
